import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { AuthService, TOKEN_EXPIRE_TIME } from '../../services/auth.service';
import { Action } from '@ngrx/store';

import {
  DeleteCookiesLoading,
  DeleteCookiesRedirect,
  DeleteCookiesSuccessLoading,
  forgotPassword,
  forgotPasswordFailure,
  forgotPasswordSuccess,
  InitAuth,
  InitAuthSet,
  logout,
  refreshTokenLoading,
  registerFailed,
  registerLoading,
  registerSuccess,
  requestSuccess,
} from './auth.actions';
import {
  RegisterInterface,
  ResponseLoginInterface,
} from '../../interfaces/auth.interface';
import { CookieCustomService } from '../../services/cookie.custom.service';
import { environment } from '../../../../environments/environment';
import { requestError } from '../login/actions';

@Injectable()
export class AuthEffect {
  constructor(
    private router: Router,
    private service: AuthService,
    private actions$: Actions,
    private cookieService: CookieCustomService
  ) {}

  public refreshToken$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(refreshTokenLoading),
      switchMap((payload: { refreshToken: string }) =>
        this.service.refreshToken(payload.refreshToken).pipe(
          map((response: ResponseLoginInterface) => {
            if (response.errorCode.code !== 0) {
              throw response;
            }
            this.cookieService.updateAuthCookie(response);
            return requestSuccess({ response: response.response });
          }),
          catchError((error: ResponseLoginInterface) => {
            return of(requestError({ error: error.errorCode }));
          })
        )
      )
    );
  });

  public authInit$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(InitAuth),
      switchMap(() => {
        const entity = this.cookieService.getItem(
          'entity_' + environment.frontToken
        );
        if (entity) {
          const response = JSON.parse(entity);

          const expiredTime = response.ttl + response.createdAt;
          const currentTime = (new Date().getTime() / 1000) | 0;
          const refreshToken = response.refreshToken;

          if (expiredTime - TOKEN_EXPIRE_TIME < currentTime) {
            return of(refreshTokenLoading({ refreshToken }));
          } else {
            return of(InitAuthSet({ entity: response }));
          }
        } else {
          return of(InitAuthSet({ entity: null }));
        }
      })
    );
  });

  public logout$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(logout),
      switchMap(() => {
        this.service.logout().subscribe();
        return of(DeleteCookiesLoading({}));
      })
    );
  });

  public deleteCookiesLoading$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(DeleteCookiesLoading),
      switchMap(async ({ admin }) => await this.cookieService.clear(admin)),
      switchMap(admin => {
        return of(DeleteCookiesRedirect({ admin }));
      })
    );
  });

  public deleteCookiesRedirect$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(DeleteCookiesRedirect),
      switchMap(() => {
        return of(DeleteCookiesSuccessLoading());
      })
    );
  });

  public forgotPassword$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(forgotPassword),
      switchMap((payload: { params: any }) =>
        this.service.forgotPassword(payload.params).pipe(
          map((response: ResponseLoginInterface) => {
            if (response.errorCode.code !== 0) {
              throw response;
            }
            return forgotPasswordSuccess();
          }),
          catchError((error: ResponseLoginInterface) => {
            return of(forgotPasswordFailure({ response: error.errorCode }));
          })
        )
      )
    );
  });

  public register$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(registerLoading),
      switchMap((payload: { params: RegisterInterface }) =>
        this.service.register(payload.params).pipe(
          map((response: ResponseLoginInterface) => {
            if (response.errorCode.code !== 0) {
              throw response;
            }
            //this.cookieService.updateAuthCookie(response);
            return registerSuccess({ response: response.response });
          }),
          catchError((error: ResponseLoginInterface) => {
            return of(registerFailed({ response: error.errorCode }));
          })
        )
      )
    );
  });
}
